/**
 * Screenshots for field test results (Joey, Nov 3 2026): shrunk here before they're sent, so each
 * is a few hundred kB however big the screen was. Re-drawing it also drops what the file carried
 * besides the picture (where and when it was taken). Words on screen stay readable: at most 2,400 px
 * on the long side, WebP at high quality (JPEG where the browser can't make WebP).
 */

export const SCREENSHOT_MAX_PX = 2400;
/** What the server takes (apps/api/src/routes/field-tests.ts MAX_SHOT_BYTES). */
const MAX_BYTES = 8 * 1024 * 1024;

export async function shrinkScreenshot(file: Blob): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('That isn’t a picture. Add a screenshot (PNG, JPEG or WebP).');
  }
  const scale = Math.min(1, SCREENSHOT_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const make = (type: string, q: number) => new Promise<Blob | null>((ok) => canvas.toBlob(ok, type, q));
  for (const q of [0.9, 0.8, 0.65]) {
    const webp = await make('image/webp', q);
    const out = webp?.type === 'image/webp' ? webp : await make('image/jpeg', q);
    if (out && out.size <= MAX_BYTES) return out;
  }
  throw new Error('That screenshot is too big even after shrinking it.');
}

/** The pictures in a paste or a drop (screenshots are pasted as files). */
export function picturesIn(data: DataTransfer | null): File[] {
  if (!data) return [];
  return [...data.files].filter((f) => f.type.startsWith('image/'));
}
